import React, { useEffect, useRef, useState } from "react";
import EventLogItem from "../../incbotFeedback/EventLogItem";
import ScreenshotCard from "../../incbotFeedback/ScreenshotCard";
import { Monitor, Camera, Mic, Speaker, Keyboard } from "lucide-react";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import { getFeedbackForm, getProfileForm } from "../../../../redux/reducer/incbotFeedbackReducer";
import { formatDate } from "../../../../utils/helper";
// [2026-09-16] ADDED — INC-1003: this CEFR report renders the SAME proctoring rows as the InCBot
// report, from the same collection, but kept its own bare sort-by-time and so never received any of
// the ordering work done for INC-1003. Reuse the InCBot view's exported helper — this file already
// imports EventLogItem and ScreenshotCard from that same folder.
// [2026-09-17] CHANGED — INC-1002/INC-1003: `renderProctoringLog` wraps `orderProctoringLog` and
// also enforces the five display rules. Old import kept for revert.
// --- OLD (pre-2026-09-17) ---
// import { orderProctoringLog } from "../../incbotFeedback/views/ProctoringLogsView";
// --- END OLD ---
// [2026-09-17] CHANGED — SEPARATE TICKET (not INC-1003): `withFeedbackSourceRow` adds one line
// naming who produced the feedback when it was NOT the candidate. Old line kept for revert:
//   import { renderProctoringLog } from "../../incbotFeedback/views/ProctoringLogsView";
// [2026-09-19] ADDED — `clampProctoringEvidenceToInterviewWindow`, so the screenshot captions in
// this view are clamped to the same window as its log. Same change as the incbot view.
import {
  clampProctoringEvidenceToInterviewWindow,
  combineRepeatedProctoringRows,
  renderProctoringLog,
  withFeedbackSourceRow,
} from "../../incbotFeedback/views/ProctoringLogsView";
import MonitorIcon from "../../../../assets/images/external-devices/monitor.svg";
import CameraVideoIcon from "../../../../assets/images/external-devices/camera_video.svg";
import MouseIcon from "../../../../assets/images/external-devices/mouse.svg";
import UsbIcon from "../../../../assets/images/external-devices/usb.svg";

const TOOLTIP_ID = "device-info-tooltip-cefr";

const TruncatedText: React.FC<{ value: string; className?: string }> = ({ value, className }) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (ref.current) {
      setIsTruncated(
        ref.current.scrollWidth > ref.current.clientWidth ||
          ref.current.scrollHeight > ref.current.clientHeight
      );
    }
  }, [value]);

  return (
    <p
      ref={ref}
      className={className}
      {...(isTruncated && {
        "data-tooltip-id": TOOLTIP_ID,
        "data-tooltip-content": value,
        "data-tooltip-place": "top",
      })}
    >
      {value}
    </p>
  );
};

const ProctoringLogsView: React.FC = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const profileForm = useSelector(getProfileForm);
  const proctoringData =
    feedbackForm?.proctoringLogs ?? feedbackForm?.proctoring ?? feedbackForm?.feedback?.proctoring;
  const externalDevices = proctoringData?.externalDevices;

  const deviceItems = [
    {
      label: "Camera",
      value: externalDevices?.camera,
      Icon: CameraVideoIcon,
      type: "img",
    },
    {
      label: "Microphone",
      value: externalDevices?.mic,
      Icon: Mic,
      type: "icon",
    },
    {
      label: "Speaker",
      value: externalDevices?.speaker,
      Icon: Speaker,
      type: "icon",
    },
    {
      label: "External Monitor",
      value: externalDevices?.monitor,
      Icon: MonitorIcon,
      type: "img",
    },
    {
      label: "Keyboard",
      value: externalDevices?.keyboard,
      Icon: Keyboard,
      type: "icon",
    },
    {
      label: "Mouse",
      value: externalDevices?.mouse,
      Icon: MouseIcon,
      type: "img",
    },
    { label: "USB", value: externalDevices?.usb, Icon: UsbIcon, type: "img" },
  ].filter((item) => item.value);

  const hasNonEmptyText = (raw: unknown) => raw != null && String(raw).trim() !== "";

  // [2026-09-16] CHANGED — INC-1003: same ordering as the InCBot report (window clamp + displayed-
  // minute ordering + spec sequence for the setup block). The `filter` is unchanged and still runs
  // first, so exactly the same rows reach the list — only their order and, for a row stamped
  // outside the interview window, the time it prints under can differ.
  // --- OLD (pre-2026-09-16) ---
  // const eventLogs =
  //   proctoringData?.messages
  //     ?.filter((message: any) => hasNonEmptyText(message?.text))
  //     ?.sort(
  //       (a: any, b: any) => new Date(a?.dateAndTime).getTime() - new Date(b?.dateAndTime).getTime()
  //     )
  //     ?.map((message: any) => {
  // --- END OLD ---
  // [2026-09-17] ADDED — INC-1003: the separator decision is made HERE, against the last row that
  // actually had a usable day, not against the immediately preceding row. A row with an unparseable
  // `dateAndTime` has an empty `dayKey`; comparing the next row against that empty value printed a
  // date separator in the middle of a single-day interview.
  let lastRenderedDayKey = "";
  // [2026-09-18] CHANGED — collapse an event that repeats inside one displayed minute. Durations
  // are summed and the count is written into the row; never a merge by elapsed span. Gated on the
  // interview date so reports a client has already read are untouched, and the suspicious-activity
  // rating is unaffected because it reads the raw api array, not this copy. Old call kept below.
  //
  //   renderProctoringLog(
  //     proctoringData?.messages?.filter((message: any) => hasNonEmptyText(message?.text))
  //   )
  const eventLogs =
    // [2026-09-18] Both wrappers apply: collapse the repeats FIRST, then append the
    // feedback-source line, which belongs at the very bottom of the log.
    withFeedbackSourceRow(
      combineRepeatedProctoringRows(
        renderProctoringLog(
          proctoringData?.messages?.filter((message: any) => hasNonEmptyText(message?.text))
        ),
        profileForm?.interviewDate
      ),
      profileForm?.endCallStatus,
      profileForm?.interviewEndDate
    )?.map((message: any) => {
      const date = new Date(message.dateAndTime);
      const time = date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      // [2026-09-17] ADDED — INC-1003: day key so the list can print a date separator when the
      // log crosses into a new day. A single-day log renders exactly as before.
      const dayKey = Number.isNaN(date.getTime()) ? "" : date.toDateString();
      const typeMap: Record<string, "success" | "warning" | "error"> = {
        green: "success",
        orange: "warning",
        red: "error",
      };
      const eventText =
        message.duration && message.duration !== "0"
          ? `${message.text} for ${message.duration} seconds`
          : message.text;
      const showDaySeparator =
        Boolean(dayKey) && lastRenderedDayKey !== "" && dayKey !== lastRenderedDayKey;
      if (dayKey) lastRenderedDayKey = dayKey;

      return {
        time,
        showDaySeparator,
        dayLabel: dayKey ? formatDate(message.dateAndTime, 1) : "",
        event: eventText,
        type: typeMap[message.type] || "error",
      };
    }) || [];

  // [2026-09-17] ADDED — INC-1003: the header above this log must be the day the log actually
  // STARTS on, not the interview's scheduled start. `profileForm.interviewDate` is mapped from
  // `interviewStartDate` (helper.tsx:356), which is rewritten on every resume, and the first
  // rendered row never prints a separator — so when the two land on different days the header
  // states a date no row below it belongs to. Measured read-only against dev on 2026-09-17: 27 of
  // 3,206 logs (0.84%). Falls back to the old value when no row has a readable date, so an empty
  // log renders exactly as before. Old line kept for revert/compare:
  //   {formatDate(profileForm?.interviewDate ?? "", 1)}
  //
  // [2026-09-17] CHANGED — INC-1003: NEW REPORTS ONLY. A report that already exists keeps the
  // header it has always had, right or wrong. This is the only change in this ticket that can alter
  // how an already-stored report LOOKS — the backend end-row fix cannot, because `callEndFn` only
  // ever appends to an interview whose `interviewEndDate` does not exist yet — so it is the only one
  // that needs a cutoff. A client who printed a report last month must not open it today and find a
  // different date at the top.
  //
  // [2026-09-22] CHANGED — the cutoff is REMOVED. Same change, same reasoning, as
  // incbotFeedback/views/ProctoringLogsView.tsx; see the full note there.
  //
  // In short: the cutoff was meant to leave an existing report looking exactly as it always had.
  // It does not, because the day SEPARATOR that shipped in the same ticket was never gated. An old
  // multi-day report therefore gained correct separators while keeping the old header, and the two
  // now contradict each other — #CAN00252 reads "Sep 9" above rows that the separators below place
  // on Sep 4 and Sep 7. Leaving the gate in place does not preserve the old report; it only keeps
  // the half of it that is wrong.
  //
  // Old lines kept for revert/compare:
  //   const PROCTORING_HEADER_FIX_FROM_MS = Date.parse("2026-09-18T00:00:00+05:30");
  //   const interviewStartedMs = new Date(profileForm?.interviewDate ?? "").getTime();
  //   const isNewReport =
  //     !Number.isNaN(interviewStartedMs) && interviewStartedMs >= PROCTORING_HEADER_FIX_FROM_MS;
  //   const proctoringHeaderDate =
  //     isNewReport && firstDatedLog?.dayLabel
  //       ? firstDatedLog.dayLabel
  //       : formatDate(profileForm?.interviewDate ?? "", 1);
  const firstDatedLog = eventLogs.find((log: { dayLabel: string }) => Boolean(log.dayLabel));
  const proctoringHeaderDate = firstDatedLog?.dayLabel
    ? firstDatedLog.dayLabel
    : formatDate(profileForm?.interviewDate ?? "", 1);

  // [2026-09-22] ADDED — one block per day. Same change and same reasoning as
  // incbotFeedback/views/ProctoringLogsView.tsx; see the note beside the render there.
  type ProctoringDayLog = {
    time: string;
    showDaySeparator: boolean;
    dayLabel: string;
    event: string;
    type: "success" | "warning" | "error";
  };
  const proctoringDayBlocks = eventLogs.reduce(
    (blocks: { label: string; logs: ProctoringDayLog[] }[], log: ProctoringDayLog) => {
      if (blocks.length === 0 || log.showDaySeparator) {
        blocks.push({ label: log.dayLabel, logs: [log] });
      } else {
        blocks[blocks.length - 1].logs.push(log);
      }
      return blocks;
    },
    []
  );

  // [2026-09-19] CHANGED — clamp the evidence images to the SAME interview window as the log above.
  // Identical change to incbotFeedback/views/ProctoringLogsView.tsx and for the identical reason:
  // the captions render from a separate array that never passed through the clamp, so a log row and
  // the caption under its own screenshot printed two different times for one event.
  //
  // Old line kept for revert/compare:
  //   const screenshots = proctoringData?.screenshots
  const screenshots =
    clampProctoringEvidenceToInterviewWindow(proctoringData?.screenshots, proctoringData?.messages)
      ?.sort(
        (a: any, b: any) => new Date(a?.dateAndTime).getTime() - new Date(b?.dateAndTime).getTime()
      )
      ?.map((screenshot: any, index: number) => {
        const dateObj = new Date(screenshot.dateAndTime);
        const formattedDate = dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        const formattedTime = dateObj
          .toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .toLowerCase();
        return {
          title: `Screenshot #${index + 1}`,
          timestamp: `${formattedDate} ${formattedTime}`,
          description:
            screenshot.duration && screenshot.duration !== "0"
              ? `${screenshot.text} for ${screenshot.duration} seconds`
              : screenshot.text,
          imageUrl: screenshot.url,
          type: screenshot.type,
        };
      }) || [];

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Proctoring Logs
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-8">
        <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-2 mb-6">
            <Monitor className="w-5 h-5 text-gray-600" />
            <h2 className="text-md md:text-lg font-semibold text-gray-900">
              Proctoring Events Log
            </h2>
          </div>
          {/* [2026-09-22] CHANGED — one BLOCK per day. Same change and same reasoning as
              incbotFeedback/views/ProctoringLogsView.tsx; the full note lives there. Old block:
              <div className="space-y-1">
                <div className="text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-4">
                  {proctoringHeaderDate}
                </div>
                <div className="border-l-2 border-blue-500 pl-4 md:space-y-1">
                  {eventLogs.map((log, index) => (
                    <React.Fragment key={index}>
                      {log.showDaySeparator && (
                        <div className="text-xs md:text-sm font-medium text-gray-700 mt-3 mb-2 md:mt-4 md:mb-4">
                          {log.dayLabel}
                        </div>
                      )}
                      <EventLogItem time={log.time} event={log.event} type={log.type} />
                    </React.Fragment>
                  ))}
                </div>
              </div> */}
          {/* [2026-09-22] CHANGED — `space-y-4`, and the per-block `mt-5 md:mt-8` dropped.
              Tailwind emits space-y as `.space-y-1 > :not([hidden]) ~ :not([hidden])`, specificity
              (0,3,0), against `.mt-5` at (0,1,0) — so the container was WINNING and every gap in
              this list was 0.25rem no matter what the label asked for. The extra room between days
              was never applied; that is why the blocks read as cramped. One honest value on the
              container replaces a margin that could not take effect.
              Old lines kept for revert/compare:
                <div className="space-y-1">
                  ... className={`... mb-2 md:mb-4 ${blockIndex > 0 ? "mt-5 md:mt-8" : ""}`} */}
          <div className="space-y-4">
            {proctoringDayBlocks.map((block, blockIndex: number) => (
              <React.Fragment key={blockIndex}>
                <div className="text-xs md:text-sm font-medium text-gray-700 mb-2 md:mb-4">
                  {blockIndex === 0 ? proctoringHeaderDate : block.label}
                </div>
                <div className="border-l-2 border-blue-500 pl-4 md:space-y-1">
                  {block.logs.map((log, index: number) => (
                    <EventLogItem key={index} time={log.time} event={log.event} type={log.type} />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3 md:gap-8">
          {deviceItems.length > 0 && (
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 mb-4 md:mb-6">
                <div className="p-1.5 bg-[#EEF2FF] rounded-md">
                  <Monitor className="w-4 h-4 md:w-5 md:h-5 text-[#133F7D]" />
                </div>
                <h2 className="text-md md:text-lg font-semibold text-gray-900">
                  External Device Information
                </h2>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                {deviceItems.map(({ label, value, Icon, type }) => (
                  <div key={label} className="flex items-start space-x-3">
                    <div className="p-2 bg-indigo-50 rounded-lg flex-shrink-0">
                      {type !== "img" ? (
                        <Icon className="w-4 h-4 text-[#133F7D]" />
                      ) : (
                        <img
                          src={Icon as unknown as string}
                          className="w-4 h-4 text-[#133F7D]"
                          alt="monitor-icon"
                        />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] text-[#65758B] font-medium">{label}</p>
                      <TruncatedText
                        value={value}
                        className="text-[11px] font-normal text-[#14181F] truncate"
                      />
                    </div>
                  </div>
                ))}
              </div>
              <Tooltip
                id={TOOLTIP_ID}
                className="!z-50 !max-w-[220px] !text-[10px] !leading-4 !whitespace-normal !break-words"
              />
            </div>
          )}

          {screenshots.length > 0 && (
            <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-2 mb-3 md:mb-6">
                <Camera className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                <h2 className="text-md md:text-lg font-semibold text-gray-900">
                  Proctoring Events Screenshots
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 md:gap-4">
                {screenshots.map(
                  (
                    screenshot: {
                      title: string;
                      timestamp: string;
                      description: string;
                      imageUrl: string;
                      type: string;
                    },
                    index: number
                  ) => (
                    <ScreenshotCard
                      key={index}
                      title={screenshot.title}
                      timestamp={screenshot.timestamp}
                      description={screenshot.description}
                      imageUrl={screenshot.imageUrl}
                      type={screenshot.type as "green" | "orange" | "red"}
                    />
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProctoringLogsView;
